import React, { Fragment, useEffect, useMemo, useState } from "react"
import { useSelector, useDispatch } from 'react-redux';
import { useRealtimeTags } from 'Core/hooks';
import { endpoints } from 'Core/defaultValues';
import { injectIntl } from 'react-intl';
import { Grid } from '@mui/material';
import GroupsList from 'Components/GroupsList';
import { TypeLayer } from 'Components/Map/providers/enums'
import { radiusFeetToMeters, findColor, getIconGroupWithAnotherColor, getMainTag, getTagsIdsArray } from 'Components/Map/utils';
import Preferences from 'Core/containers/Preferences';
import { Select, Button } from 'Components';
import { OrderData } from 'Core/data/Helpers';
import styles from 'Assets/sass/core/vars.scss';
import { MenuComponent } from 'Components/Menu';
import _ from 'lodash';

import CreateEdit from '../landmarkCreateEdit';
import LandmarkImport from '../landmarkImport';
import Delete from '../landmarkDelete';
import { Checkbox } from '@gpstrackit/gtc-react-controls';

const pathFile = `https://s3.amazonaws.com/${endpoints.PUBLIC_REPOSITORY_CODE}`

/*********************************************************
 * Redux
*********************************************************/
import {
    hideLandmarkGroups,
    mapGetLandmarkTags,
    mapLandmarksOpenEditCreateModal,
    mapLandmarkImportModal,
    mapLandmarksOpenDeleteModal,
    savePreference,
    getLandmarksAndGeofences as getKmls
} from 'Redux/actions';

const ROWS_PER_PAGE = 25;

const LandmarkList = (props) => {

    const { intl } = props;
    const { messages } = intl;
    const [data, setData] = useState([])
    const [selectedItem, setSelectedItem] = useState(null);
    const [preferences, setPreferences] = useState();
    const [showMenu, setShowMenu] = useState({});
    const [selectedAll, setSelectedAll] = useState(false);
    const dispatch = useDispatch()
    //const preferencesKey = 'landmarkGeofenceView';
    const preferencesKey = 'realtimeMaps';

    /*********************************************************
     * Use selector
    *********************************************************/
    
    const loadedJsonFiles = useSelector(state => state.catalogsRealtimeRedux.loadedJsonFiles);
    const jsonLandmark = useSelector((state) => state.catalogsRealtimeRedux.landmarks);
    const loadingLandmarkJson = useSelector((state) => state.catalogsRealtimeRedux.loadingLandmarks);
    const { authenticatedId } = useSelector(state => state.securityRedux);
    const { open: openLandmarkCreateEdit, openImport } = useSelector((state) => state.realtimeMapLandmarkCreateEditModuleRedux);
    const tagIds = useSelector((state) => state.securityRedux?.user?.tagIds);
    const isMasterUser = useMemo(() => tagIds?.includes(-99), [tagIds]);

    const {
        response,
        loadingResponse,
    } = useSelector((state) => state.realtimeMapLandmarkDeleteModuleRedux);
    const tags  = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags', 'color', 'parentId'] });
    const hiddenLandmarkGroups = useSelector(state => state.realtimeMapLandmarkModuleRedux.hiddenLandmarkGroups);

    const returnServer = useSelector(state => state.preferencesRedux.returnServer);
    const preferencesUser = useSelector(state => state.preferencesRedux.preferencesUser);

    const haveUntagged = !!(
        (jsonLandmark && Array.isArray(jsonLandmark) && jsonLandmark.some(landmark => {
            const tagsIds = getTagsIdsArray(landmark.tagsId);
            return (tagsIds.includes('-1') || tagsIds.length === 0);
        })) ||
        (tags && Array.isArray(tags) && tags.some(tag => tag.id === -1)) ||
        (!tags || tags.length === 0)
    );

    /*********************************************************
     * Starting with load groups
    *********************************************************/
    useEffect(() => {
        dispatch(mapGetLandmarkTags())
        if(loadedJsonFiles && !loadedJsonFiles?.length > 0){
            dispatch(getKmls())
        }
    }, [])

    useEffect(() => {
        if (preferencesUser) {
            let preferencesKeyTemp = preferencesUser.find(x => x.preferenceKey === preferencesKey);
            let preferencesJSON = preferencesKeyTemp && preferencesKeyTemp.preferenceValue != "" && preferencesKeyTemp.preferenceValue || "{}";
            preferencesJSON = JSON.parse(preferencesJSON);
    
            setPreferences(preferencesJSON)
        }
    }, [returnServer]);


    /*********************************************************
     * Get the icon url from landmarks or landmark group
    *********************************************************/
    const getIconLandmark = (landmark) => {

        if (!tags || tags.length === 0) {
            const landmarkUntagged = styles.color_untagged?.replace("#", "");
            return { color: landmarkUntagged, icon: `${pathFile}/kmls/flagicon/${landmarkUntagged}.png` }
        }

        const landmarkGroup = tags.find((item) => getTagsIdsArray(landmark.tagsId).includes(item.id.toString()))
        let landmarkColor = landmark.color ? landmark.color : landmarkGroup?.color || ''
        let landmarkIcon = landmarkGroup && landmarkGroup.iconName ? landmarkGroup.iconName : '' //get icon from landmark group
        landmarkIcon = landmark.iconName ? landmark.iconName : getIconGroupWithAnotherColor(landmarkIcon, landmark.color)// get icon from landmark

        if (!landmarkIcon) {
            landmarkColor = findColor(landmarkColor.replace("#", ""));
            landmarkIcon = `${pathFile}/kmls/flagicon/${landmarkColor}.png`
        } else {
            landmarkIcon = `${pathFile}/${landmarkIcon}`
        }

        return { color: landmarkColor, icon: landmarkIcon }
    }


    /*********************************************************
     * Use effect ot load landmark by preferences
    *********************************************************/
    useEffect(() => {
        let landmarkData = [];
        if (haveUntagged) {
            landmarkData.push({
                id: -1,
                name: messages['untagged'],
                color: styles.color_untagged,
                visible: true,
                open: true,
                items: []
            })
        }

        if (tags && tags.length > 0) {

            landmarkData.push(
                ...tags
                    .filter(item => item.id !== -1 && (isMasterUser || tagIds?.includes(Number(item.id))))
                    .map((item) => ({
                        ...item,
                        name: item.label,
                        visible: true,
                        open: true,
                        items: []
                    }))
            );
                
            if (preferences?.hiddenLandmarkGroups?.length) {
                landmarkData = landmarkData?.map(ld => {
                    if (preferences?.hiddenLandmarkGroups?.includes(ld?.id)) {
                        return {
                            ...ld,
                            visible: false
                        }
                    }
                    return ld;
                });
            } 

            if (preferences?.condensedLandmarkTagIds?.length) {
                landmarkData = landmarkData?.map(ld => {
                    if (preferences?.condensedLandmarkTagIds?.includes(ld?.id)) {
                        return {
                            ...ld,
                            open: false
                        }
                    }
                    return ld;
                });
            }

            landmarkData.sort((a, b) => {
                // untagged is first
                if (a.id === -1) return -1;
                if (b.id === -1) return 1;
            
                // sort by name attribute
                if (a.name?.toString()?.toLowerCase() > b.name?.toString()?.toLowerCase()) return 1;
                if (b.name?.toString()?.toLowerCase() > a.name?.toString()?.toLowerCase()) return -1;
                return 0;
            });

            if (haveUntagged) {
                let untaggedLandmarks = landmarkData?.find(item => item?.id == 0);
                if (untaggedLandmarks) {
                    landmarkData = landmarkData.filter(item => item?.id != 0);
                    landmarkData?.unshift(untaggedLandmarks);
                }
            }

            landmarkData = getItemsOnTags(landmarkData);
            setData(landmarkData);
        } else if (haveUntagged) {
            landmarkData = getItemsOnTags(landmarkData);
            setData(landmarkData);
        }
    }, [tags, jsonLandmark, tagIds, loadingLandmarkJson ,preferences?.hiddenLandmarkGroups, preferences?.condensedLandmarkTagIds ])

    /*********************************************************
     * Use effect ot load landmarks by groups
    *********************************************************/
    useEffect(() => {
        if (data && data.length > 0 && jsonLandmark && jsonLandmark.length > 0) {
            const landmarkTagsData = getItemsOnTags(data);
            landmarkTagsData && landmarkTagsData?.length > 0 && setData([...landmarkTagsData]);
        }
    }, [jsonLandmark, tags, tagIds]);

    /*********************************************************
     * Add the landmarks into the tags elements
    *********************************************************/
    const  getItemsOnTags = (tags = []) => {

        if(!jsonLandmark || jsonLandmark?.length <= 0) return tags;

        const allowedTags = isMasterUser ? tags : tags.filter(item => tagIds?.includes(Number(item.id)));

        const landmarkTagsData = [...allowedTags];

        //add the untagged group
        if (haveUntagged && !landmarkTagsData.find(item => item?.id == -1)) {
            landmarkTagsData.push({
                id: -1,
                name: messages['untagged'],
                color: styles.color_untagged,
                visible: true,
                open: true,
                items: []
            })
        }

        // Groups that have at least one landmark specifically assigned to them (not via -99)
        const groupsWithSpecificLandmarks = new Set(
            jsonLandmark.flatMap(l => {
                if (!l.tagsId) return [];
                return l.tagsId.split(',').filter(id => id !== '-99' && id !== '-1' && id !== '0' && id !== '');
            })
        );
        const hasSpecificAssignments = groupsWithSpecificLandmarks.size > 0;

        landmarkTagsData.forEach((item) => {
            let landmarkElements = jsonLandmark.filter((landmark) => {
                if (landmark?.tagsId) {
                    const tagsIds = landmark.tagsId.split(',');
                    // -1: untagged only | -99 (all tags): all groups if no specific assignments exist, otherwise only groups that have specific assignments
                    return tagsIds?.includes(item.id.toString()) || (tagsIds?.includes('-1') && item.id == -1) || (tagsIds?.includes('-99') && item.id != -1 && (!hasSpecificAssignments || groupsWithSpecificLandmarks.has(item.id.toString())));
                } else if (haveUntagged && item.id == -1) { //untagged group
                    return true;
                }
                return false;
            })
            //find color null
            landmarkElements = landmarkElements.map((landmark) => {
                return {
                    ...landmark,
                    color: landmark.color ? landmark.color : item.color,
                    icon: getIconLandmark(landmark).icon
                }
            })

            landmarkElements.sort((a, b) => { //Order by Alphabetically
                if (a.name?.toString()?.toLowerCase() > b.name?.toString()?.toLowerCase()) return 1;
                if (b.name?.toString()?.toLowerCase() > a.name?.toString()?.toLowerCase()) return -1;
                return 0;
            });

            item.items = [...landmarkElements];
        });

        //remove tags that have no landmarks assigned
        const filteredItems = landmarkTagsData.filter(item => item.items.length > 0);
        landmarkTagsData.splice(0, landmarkTagsData.length, ...filteredItems);

        if (haveUntagged) {
            //order untagged in first place
            landmarkTagsData.sort((a, b) => {
                // untagged group is first
                if (a.id === -1) return -1;
                // untagged group is first
                if (b.id === -1) return 1;
            
                // sort by name attribute
                if (a.name?.toString()?.toLowerCase() > b.name?.toString()?.toLowerCase()) return 1;
                if (b.name?.toString()?.toLowerCase() > a.name?.toString()?.toLowerCase()) return -1;
                return 0;
            });
        }
        return landmarkTagsData;
    }

    /*********************************************************
     * Draw the landmarks into the map
    *********************************************************/
    const onItemClicked = (item) => {
        const isInfoBubbleOpened = props?.mapRef?.current?.getOpenInfoWindow();
        if (!_.isEqual(selectedItem, item) || !isInfoBubbleOpened) {
            setSelectedItem(item);
            const coordinates = new google.maps.LatLng(item.latitude, item.longitude); // eslint-disable-line no-undef
            const radius = item.shape == "circle" ? item.radius : 0
            const coordinatesCenter = radius > 0 ? [{ lat: item.latitude, lng: item.longitude }] : item.coordinates
            props.mapRef.current.centerByFitBounds(coordinatesCenter, radius)
            const mapObject = {
                id: item?.id,
                position: coordinates,
                label: item?.name,
                icon: {
                    rotation: 0
                },
                layerName: TypeLayer.Landmark,
            }
            props.mapRef.current.modalPopupEvent(mapObject, mapObject);

            const tag = getMainTag(item, tags, true)

            //Always show the landmark on map
            props.mapRef.current.removeLayer(TypeLayer.TemporalObjects);
            props.mapRef.current.drawShapeObjectTempOnMap(item, tag)
        }
    }

    /*********************************************************
     * get the landmarks by petition / load the landmark items
     * into the tree
     *********************************************************/
    const onTitleContainerClicked = (groupId, expanded) => {
        onExpanded(groupId, expanded)
    }

    /*********************************************************
     * open or close the list items.
     *********************************************************/
    const onExpanded = (groupId, expanded) => {
        const landmarkGroupItems = data.map(item => {
            if (item.id == groupId) {
                return {
                    ...item,
                    open: expanded
                }
            }
            return item;
        });

        setData([...landmarkGroupItems]);

        const newlandmarkGroupItems = landmarkGroupItems?.filter(i => !i.open);
        const condensedLandmarkTagIds = newlandmarkGroupItems?.map(i => i?.id);

        const preferenceValue = { ...preferences, condensedLandmarkTagIds: condensedLandmarkTagIds };
        dispatch(savePreference({ entityName: "User", entityId: authenticatedId, preferenceKey: preferencesKey, preferenceValue: preferenceValue}));
        setPreferences(preferenceValue)
    }

    /*********************************************************
     * show the groups into the realtime maps (KML files)
     *********************************************************/
    const onGroupCheckChanged = (groupId, checked) => {
        const landmarksGItems = data.map(item => {
            if (item.id == groupId) {
                return {
                    ...item,
                    visible: checked
                }
            }
            return item;
        });

        setData([...landmarksGItems]);
        let hiddenLandmarkTags = landmarksGItems.filter(item => !item.visible);
        dispatch(hideLandmarkGroups(hiddenLandmarkTags));

        let newHiddenLandmarkTags = hiddenLandmarkTags.map(hiddenLandmarkTag => hiddenLandmarkTag?.id);

        
        const preferenceValue = { ...preferences, hiddenLandmarkGroups: newHiddenLandmarkTags };
        dispatch(savePreference({ entityName: "User", entityId: authenticatedId, preferenceKey: preferencesKey, preferenceValue: preferenceValue}));
        setPreferences(preferenceValue)
    }

    /*********************************************************
     * Expand all tags into the view
    *********************************************************/
    const onExpandSwitchAll = (checked) => {
        const dataTags = [...data]
        let landmarkData = dataTags.map((item) => {
            return {
                ...item,
                open: checked
            };
        })
        landmarkData.map((item) => {
            onExpanded(item.id, checked)
        })

        setData([...landmarkData])

        const preferenceValue = { ...preferences, landmarkExpandAll: checked };
        dispatch(savePreference({ entityName: "User", entityId: authenticatedId, preferenceKey: preferencesKey, preferenceValue: preferenceValue}));
        setPreferences(preferenceValue)
    }

    const findLandmarkOnMap = (item) => {
        const isInfoBubbleOpened = props?.mapRef?.current?.getOpenInfoWindow();
        if (item || !isInfoBubbleOpened) {
            const coordinates = new google.maps.LatLng(item.latitude, item.longitude); // eslint-disable-line no-undef
            const radius = item.shape == "circle" ? item.radius : 0
            const coordinatesCenter = radius > 0 ? [{ lat: item.latitude, lng: item.longitude }] : item.coordinates
            props.mapRef.current.centerByFitBounds(coordinatesCenter, radius)
            const mapObject = {
                id: item?.id,
                position: coordinates,
                label: item?.name,
                icon: {
                    rotation: 0
                },
                layerName: TypeLayer.Landmark
            }
            props.mapRef.current.modalPopupEvent(mapObject, mapObject);

            //Always show the landmark on map
            props.mapRef.current.removeLayer(TypeLayer.TemporalObjects);
            props.mapRef.current.drawLandmark({ ...item, hideMarker: true }, radiusFeetToMeters(radius), coordinatesCenter, true)
        }
    }

    const onSelectedTags = (value) => {
        const excludeItems = [] //select all exclude items is empty
        if(!value){
            //unselect all
            data.forEach((item) => {
                excludeItems.push(item.id)
            })
            dispatch(hideLandmarkGroups(data));
        }else{
            dispatch(hideLandmarkGroups([]));
        }
        setSelectedAll(value)
    
        //save value on preference
        const preferenceValue = { ...preferences, hiddenLandmarkGroups: excludeItems };
        dispatch(savePreference({ entityName: "User", entityId: authenticatedId, preferenceKey: preferencesKey, preferenceValue: preferenceValue}));
        setPreferences(preferenceValue);
    }

    return (
        <Grid container spacing={0}>
            <Grid item sm={12}>
                <Preferences
                    preferencesKey={preferencesKey}
                    onInitialPreferences={(data) => {
                        if(!preferences){
                            setPreferences({landmarkExpandAll: false, ...data });
                        }
                    }}>
                    <GroupsList
                        showSearch
                        useDelay
                        isExpandAll={false}
                        expandAllProp={preferences?.landmarkExpandAll || false}
                        messages={messages}
                        loading={loadingLandmarkJson}
                        onItemClicked={onItemClicked}
                        onTitleContainerClicked={onTitleContainerClicked}
                        onGroupCheckChanged={onGroupCheckChanged}
                        groupsData={data}
                        messageNoItems={messages["maputilities_vehiclelist_no_items"]}
                        onExpanded={onExpanded}
                        onExpandSwitchAll={onExpandSwitchAll}
                        offset={0}
                        itemsPerPage={ROWS_PER_PAGE}
                        totalItems={data.length}
                        height={600}
                        bodyHeight="calc(100vh - 470px)"
                        overflowY="auto"
                        renderActions={(
                            <Fragment>
                                <div style={{ display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',}}>
                                    <Button
                                        type="icon"
                                        icon="add_box"
                                        tooltip={messages['add']}
                                        size="large"
                                        fontSize="inherit"
                                        onClick={() => {
                                            dispatch(mapLandmarksOpenEditCreateModal(true));
                                        }}
                                    />

                                    <Button
                                        type="icon"
                                        icon="file_upload"
                                        size="large"
                                        fontSize="inherit"
                                        tooltip={messages['map_landmark_import']}
                                        onClick={() => {
                                            dispatch(mapLandmarkImportModal(true));
                                        }}
                                    />
                                    <div style={{ width: 200, paddingLeft: 50, whiteSpace: "nowrap"}}>
                                        <label 
                                            style={{ textDecoration: 'underline', color: '#4b4b4b', cursor: 'pointer' }} 
                                            className='vehicleList-showall-value'
                                            onClick={()=> {
                                                onSelectedTags(true)
                                            }}
                                        >
                                            {messages['maputilities_vehiclelist_showAll']}
                                        </label>
                                        <label style={{ padding: 5 }}>{" | "}</label>
                                        <label 
                                            style={{ textDecoration: 'underline', color: '#4b4b4b', cursor: 'pointer' }} 
                                            className='vehicleList-showall-value'
                                            onClick={()=> {
                                                onSelectedTags(false)
                                        }}>
                                            {messages['maputilities_vehiclelist_hiddenAll']}
                                        </label>
                                    </div>
                                </div>
                            </Fragment>
                        )}
                        onMouseEnter={(item) => {
                            let newShowMenu = { ...showMenu };
                            newShowMenu[item?.id] = true;
                            setShowMenu(newShowMenu);
                        }} 
                        onMouseLeave={(item) => {
                            let newShowMenu = { ...showMenu };
                            newShowMenu[item?.id] = false;
                            setShowMenu(newShowMenu);
                        }} 
                        renderItemActions={(item) => {
                            return (
                                <div>
                                    {
                                        showMenu[item?.id] &&
                                        <MenuComponent
                                            options={[
                                                messages['map_landmark_edit'],
                                                messages['map_landmark_delete']
                                            ]}
                                            icon={'more_vert'}
                                            className="context-menu"
                                            onClick={(type, value) => {
                                                if (type === messages['map_landmark_edit']) {
                                                    findLandmarkOnMap(value);
                                                    dispatch(mapLandmarksOpenEditCreateModal(true, value?.id));
                                                } else if (type === messages['map_landmark_delete']) {
                                                    dispatch(mapLandmarksOpenDeleteModal(true, [value]));
                                                }
                                            }}
                                            value={item}
                                        />
                                    }
                                </div>
                            )
                        }}
                        clearFilterAfterAction={response && !loadingResponse}
                    >
                    </GroupsList>
                </Preferences>
            </Grid>

            {
                openLandmarkCreateEdit && <CreateEdit mapRef={props?.mapRef} />
            }

            {
                openImport && <LandmarkImport />
            }

            <Delete mapRef={props.mapRef}/>
        </Grid>
    )
}

export default injectIntl(LandmarkList)
